import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Check, Loader2, Gift, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { SupportModal } from "@/components/SupportModal";

const PLANS = [
  { id: "starter", name: "Starter", price: "$49", period: "/mo", desc: "For solo devs & small studios shipping their first live game backend.",
    features: ["Digital twin agent access", "Golang + PHP generation", "1 Notebook API key", "MySQL & Redis templates", "Community support"] },
  { id: "studio", name: "Studio", price: "$199", period: "/mo", popular: true, desc: "For teams running real-time, turn-based games at scale.",
    features: ["Everything in Starter", "AI Workflow + Automation APIs", "Unlimited API keys", "k8s manifests & autoscaling", "Priority 24/7 support"] },
  { id: "enterprise", name: "Enterprise", price: "Custom", period: "", desc: "Dedicated twin tuning, SSO, and white-glove onboarding.",
    features: ["Everything in Studio", "Dedicated architecture reviews", "On-prem / private cloud", "SLA & security hardening", "Named engineer"] },
];

export const Pricing = () => {
  const { user, login } = useAuth();
  const [loading, setLoading] = useState(null);
  const [support, setSupport] = useState(false);
  const [trialing, setTrialing] = useState(false);

  const startTrial = async () => {
    if (!user) { login(); return; }
    setTrialing(true);
    try {
      await api.post("/payments/start-trial");
      toast.success("Your 7-day free trial is active! Redirecting…");
      setTimeout(() => (window.location.href = "/workspace"), 900);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start your trial.");
      setTrialing(false);
    }
  };

  const ctaLabel = (p) => {
    if (loading === p.id) return <Loader2 className="w-4 h-4 animate-spin" />;
    if (p.id === "enterprise") return "Contact sales";
    return "Subscribe";
  };

  const subscribe = async (plan) => {
    if (plan.id === "enterprise") { setSupport(true); return; }
    if (!user) { login(); return; }
    setLoading(plan.id);
    try {
      const res = await api.post("/payments/checkout", { package_id: plan.id, origin_url: window.location.origin });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout. Please try again.");
      setLoading(null);
    }
  };

  return (
    <section id="pricing" className="py-24 px-4 sm:px-6 lg:px-8 bg-background">
      <div className="max-w-7xl mx-auto">
        <div className="max-w-2xl mb-14">
          <span className="text-xs font-mono uppercase tracking-widest text-emerald-400">Pricing</span>
          <h2 className="mt-3 font-display text-3xl lg:text-4xl font-bold tracking-tight text-slate-100">
            Plans that scale with your game
          </h2>
          <p className="mt-4 text-slate-400 leading-relaxed">Live subscriptions — pay securely by card (Visa / Mastercard). Cancel anytime.</p>
        </div>

        <div data-testid="free-trial-banner"
          className="relative glass rounded-2xl border border-emerald-500/40 p-6 sm:p-7 mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-5 glow-emerald overflow-hidden">
          <div className="absolute -top-16 -left-10 w-56 h-40 bg-emerald-500/15 blur-[90px] pointer-events-none" />
          <div className="flex items-start gap-4 relative">
            <div className="w-12 h-12 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center shrink-0">
              <Gift className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-display text-xl font-semibold text-white">7-day free trial</h3>
                <span className="text-[10px] font-mono uppercase tracking-widest text-emerald-400 border border-emerald-500/30 rounded-full px-2.5 py-0.5">$0</span>
              </div>
              <p className="mt-1 text-sm text-slate-400 max-w-md">Full access to the digital twin agent and API connectors for 7 days. No card required.</p>
            </div>
          </div>
          <Button onClick={startTrial} disabled={trialing} data-testid="start-free-trial-btn"
            className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold h-11 px-6 shrink-0 group">
            {trialing ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Start free trial <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" /></>}
          </Button>
        </div>

        <div className="grid md:grid-cols-3 gap-6 items-stretch">
          {PLANS.map((p) => (
            <div key={p.id} data-testid={`pricing-card-${p.id}`}
              className={`relative glass rounded-2xl border p-7 flex flex-col shadow-xl shadow-black/30 transition-colors duration-300 ${
                p.popular ? "border-emerald-500/60 glow-emerald" : "border-slate-800 hover:border-slate-700"}`}>
              {p.popular && (
                <span className="absolute -top-3 left-7 text-[10px] font-mono uppercase tracking-widest text-slate-950 bg-emerald-400 rounded-full px-3 py-1">Most popular</span>
              )}
              <h3 className="font-display text-xl font-semibold text-white">{p.name}</h3>
              <div className="mt-3 flex items-end gap-1">
                <span className="font-display text-4xl font-bold text-white">{p.price}</span>
                <span className="text-slate-500 mb-1 text-sm">{p.period}</span>
              </div>
              <p className="mt-3 text-sm text-slate-400 leading-relaxed">{p.desc}</p>
              <ul className="mt-6 space-y-3 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-slate-300">
                    <Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <Button onClick={() => subscribe(p)} disabled={loading === p.id} data-testid={`subscribe-${p.id}-btn`}
                className={`mt-7 rounded-full h-11 font-semibold ${
                  p.popular ? "bg-emerald-500 hover:bg-emerald-400 text-slate-950"
                            : "bg-slate-800 hover:bg-slate-700 text-white"}`}>
                {ctaLabel(p)}
              </Button>
            </div>
          ))}
        </div>
      </div>
      <SupportModal open={support} onClose={() => setSupport(false)} />
    </section>
  );
};
